import { makeStyles, shorthands, tokens } from "@fluentui/react-components";
import { $cloud70, $sky60 } from "../../../cssjs/colors";

export const notificationGridColumns =
  "120px minmax(100px, 0.8fr) 78px 100px minmax(160px, 1.45fr) 124px 100px";

export const useNotificationHistoryStyles = makeStyles({
  root: {
    minWidth: 0,
    height: "calc(100vh - 182px)",
    display: "flex",
    flexDirection: "column",
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    padding: `${tokens.spacingHorizontalL} ${tokens.spacingHorizontalXL}`,
  },

  rootTeams: {
    height: "calc(100vh - 136px)",
  },
});

export const useNotificationHistoryHeaderStyles = makeStyles({
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalM,
  },
});

export const useNotificationFiltersStyles = makeStyles({
  filtersRow: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: tokens.spacingHorizontalL,
    rowGap: tokens.spacingVerticalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    backgroundColor: "#FAFAFA",
    boxShadow: "0px 0px 2px 0px #00000040",
  },

  filterGroup: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: "20px",
    rowGap: tokens.spacingVerticalS,
  },
  filterField: {
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    minWidth: 0,
  },

  dateRange: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalS,
  },

  dateField: {
    width: "152px",
    "& .ms-TextField-fieldGroup": {
      height: "32px",
      border: `1px solid ${$cloud70}}`,
      borderRadius: tokens.borderRadiusMedium,
    },
  },

  filterLabel: {
    paddingBottom: tokens.spacingVerticalXS,
  },

  dropdown: {
    minWidth: "110px",
    maxWidth: "150px",
    height: "32px",
    border: `1px solid ${$cloud70}`,
  },

  sortField: {
    minWidth: "80px",
    maxWidth: "145px",
  },

  sortDropdown: {
    minWidth: "140px",
    maxWidth: "150px",
    border: `1px solid ${$cloud70}`,
  },

  resetButton: {
    fontSize: tokens.fontSizeBase400,
    marginLeft: "auto",
    alignSelf: "flex-end",
    color: $sky60,
    fontWeight: tokens.fontWeightSemibold,
    cursor: "pointer",
    padding: 0,
    minWidth: 0,
  },
});

export const useNotificationListStyles = makeStyles({
  list: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    marginTop: tokens.spacingVerticalS,
    overflow: "hidden",
  },

  cardList: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    overflowY: "auto",
    gap: tokens.spacingVerticalXS,
    padding: `0 ${tokens.spacingHorizontalXXS} ${tokens.spacingHorizontalM}`,
    scrollbarGutter: "stable",
  },

  emptyState: {
    textAlign: "center",
    color: tokens.colorNeutralForeground2,
    ...shorthands.padding(tokens.spacingVerticalXXL, tokens.spacingHorizontalL),
  },
});

export const useNotificationListHeaderStyles = makeStyles({
  listHeader: {
    display: "grid",
    position: "sticky",
    top: 0,
    zIndex: 1,
    flexShrink: 0,
    gridTemplateColumns: notificationGridColumns,
    alignItems: "center",
    columnGap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
    color: tokens.colorNeutralForeground1,
    backgroundColor: tokens.colorNeutralBackground1,
  },

  headerCell: {
    minWidth: 0,
    fontWeight: tokens.fontWeightSemibold,

    "&:not(:first-child)": {
      borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
      paddingLeft: tokens.spacingHorizontalS,
    },
  },
});

export const useNotificationCardStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    ":hover": {
      ...shorthands.borderColor(tokens.colorBrandStroke1),
    },

    ":focus-within": {
      ...shorthands.outline("2px", "solid", tokens.colorStrokeFocus2),
    },
  },

  cardRow: {
    display: "grid",
    gridTemplateColumns: notificationGridColumns,
    alignItems: "start",
    columnGap: tokens.spacingHorizontalS,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalS}`,
  },

  cell: {
    minWidth: 0,
    paddingLeft: tokens.spacingHorizontalS,
  },

  messageCell: {
    minWidth: 0,
  },

  messagePreview: {
    display: "-webkit-box",
    WebkitBoxOrient: "vertical",
    WebkitLineClamp: 3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    overflowWrap: "anywhere",
    lineHeight: tokens.lineHeightBase300,

    // Fallback height for browsers where line clamp is unavailable.
    maxHeight: `calc(${tokens.lineHeightBase300} * 3)`,
  },

  emptyValue: {
    color: tokens.colorNeutralForeground3,
    fontStyle: "italic",
  },

  time: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
  },

  alert: {
    display: "flex",
    alignItems: "flex-start",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },

  alertIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    width: "28px",
    height: "28px",
    color: tokens.colorBrandForeground1,
    backgroundColor: tokens.colorBrandBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },

  alertIconImage: {
    width: "20px",
    height: "20px",
    flexShrink: 0,
  },

  alertTitle: {
    paddingTop: "4px",
    fontWeight: tokens.fontWeightSemibold,
    overflowWrap: "anywhere",
  },

  severityBadge: {
    whiteSpace: "nowrap",
  },

  categoryTag: {
    width: "fit-content",
    minWidth: "56px",
    justifyContent: "center",
  },

  message: {
    lineHeight: tokens.lineHeightBase300,
    overflowWrap: "anywhere",
  },

  impact: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    lineHeight: tokens.lineHeightBase300,
  },

  // action: {
  //   display: "flex",
  //   justifyContent: "flex-end",
  // },

  showHideButton: {
    padding: 0,
    minWidth: 0,
    width: "100%",
    maxWidth: "100%",
    height: "auto",
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: tokens.lineHeightBase300,
    textAlign: "center",
    color: $sky60,
    fontWeight: tokens.fontWeightSemibold,
    cursor: "pointer",

    "& .fui-Button__content": {
      whiteSpace: "normal",
      overflow: "visible",
      textOverflow: "clip",
      overflowWrap: "normal",
    },
  },

  details: {
    backgroundColor: tokens.colorBrandBackground2,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    ...shorthands.padding(
      tokens.spacingVerticalL,
      tokens.spacingHorizontalXL,
      tokens.spacingVerticalXL
    ),
  },

  detailsText: {
    display: "block",
    whiteSpace: "pre-wrap",
    overflowWrap: "anywhere",
    lineHeight: tokens.lineHeightBase400,
  },

  detailsContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    lineHeight: tokens.lineHeightBase400,
    ...shorthands.padding(tokens.spacingVerticalL, tokens.spacingHorizontalL),
    borderRadius: tokens.borderRadiusMedium,
  },
});
